import { FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createOrganisation } from '../../firebase';
import { Timestamp } from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';
import { csvContent } from '../../interfaces';
import { toast } from 'react-toastify';
import { Container, Form, InputGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import FABBackButton from '../../components/FABBackButton';
import { generatePromoPrefix } from '../../utils/helperFunctions';

const CreateOrganisation = () => {
  const navigate = useNavigate();
  const [error, setError] = useState<{ name: boolean; seats: boolean }>({ name: false, seats: false });
  const { t } = useTranslation();
  const [seats, setSeats] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [admins, setAdmins] = useState<string>('');
  const [members, setMembers] = useState<string>('');
  const [promoCode, setPromoCode] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const adminPickerRef = useRef<HTMLInputElement>(null);
  const memberPickerRef = useRef<HTMLInputElement>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [promoPrefix] = useState(generatePromoPrefix());

  const isValid = () => {
    const temp = { name: false, seats: false };
    if (name.trim() === '') temp.name = true;

    if (totalCount > (!isNaN(parseInt(seats, 10)) ? parseInt(seats, 10) : 0)) temp.seats = true;
    setError(temp);
    return temp;
  };

  const adminsArrayRef = useRef<string[]>();
  const membersArrayRef = useRef<string[]>();
  useEffect(() => {
    const adminsArray = admins.trim() === '' ? [] : admins.trim().split(',');
    const membersArray = members.trim() === '' ? [] : members.trim().split(',');
    adminsArrayRef.current = adminsArray;
    membersArrayRef.current = membersArray;
    setTotalCount(adminsArray.length + membersArray.length);
  }, [admins, members]);
  const onFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const validation = isValid();
    if (validation.name || validation.seats) return;
    if (promoPrefix.trim() === '') {
      toast.error(t('organisations.prefix-error'));
      return;
    }

    const _seats = !isNaN(parseInt(seats, 10)) ? parseInt(seats, 10) : 0;
    setLoading(true);
    try {
      const _org = await createOrganisation({
        createdAt: Timestamp.now(),
        admins: admins.trim() === '' ? [] : admins.split(','),
        members: members.trim() === '' ? [] : members.split(','),
        name,
        seats: _seats,
        promoCode: promoPrefix + '-' + promoCode
      });
      toast.success(t('organisations.org-created'));
      setName('');
      setSeats('');
      setAdmins('');
      setMembers('');
      setPromoCode('');
      navigate(`/orgs/${_org.id}`);
    } catch (e) {
      console.log('Create organisation failed:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, type: csvContent) => {
    if (e.target.files) {
      try {
        const file = e.target.files[0];
        const fileUrl = URL.createObjectURL(file);
        const response = await fetch(fileUrl);
        const text = await response.text();
        const lines = text.split(/\r\n|\n|\r/);
        const _data = lines.map((line) => line.split(','));
        if (type === csvContent.admins) {
          setAdmins(_data.flat().toString());
        }
        if (type === csvContent.members) {
          setMembers(_data.flat().toString());
        }
        e.target.files = null;
        e.target.value = '';
      } catch (error) {
        console.error(error);
      }
    }
  };

  return (
    <Container>
      <div>
        <FABBackButton to={'/orgs'} />
        <h1 className='h1 mb-3'>{t('organisations.createNew')}</h1>
        <div className='d-flex flex-row justify-content-between align-items-center m-4'></div>
        <div className='text-start'>
          <form onSubmit={onFormSubmit} noValidate>
            <div className='form-group'>
              <label className='d-flex mt-3' htmlFor='organisationName'>
                {t('organisations.organisationName')}
              </label>
              <input
                type='text'
                className={`form-control my-2 ${error.name ? 'is-invalid' : ''}`}
                id='organisationName'
                aria-describedby='emailHelp'
                placeholder={t('organisations.organisationName')}
                required
                value={name}
                onChange={(e) => {
                  if (error.name) setError({ ...error, name: false });
                  setName(e.target.value);
                }}
              />
              <div className='invalid-feedback'>{t('organisations.name-error')}</div>
            </div>
            <div className='form-group'>
              <label htmlFor='organisationSeats' className='d-flex mt-3'>
                {t('organisations.seats')}
              </label>
              <input
                type='number'
                className={`form-control my-2 ${error.seats ? 'is-invalid' : ''}`}
                id='organisationSeats'
                placeholder={t('organisations.seats')}
                value={seats}
                min={0}
                onChange={(e) => {
                  if (error.seats) setError({ ...error, seats: false });
                  setSeats(e.target.value);
                }}
              />
              <div className='invalid-feedback'>{t('organisations.not-enough-seats')}</div>
            </div>

            <Form.Label htmlFor='promo-code' className='d-flex mt-3'>
              {t('organisations.promoCode')}
            </Form.Label>
            <InputGroup className='mb-3 my-2'>
              <InputGroup.Text
                id='basic-addon3'
                style={{
                  backgroundColor: '#0e1422',
                  border: 'none',
                  borderTopLeftRadius: '40px',
                  borderBottomLeftRadius: '40px'
                }}
              >
                {promoPrefix}-
              </InputGroup.Text>
              <Form.Control
                id='promo-code'
                aria-describedby='basic-addon3'
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
              />
            </InputGroup>
            <div className='form-group'>
              <label htmlFor='organisationAdmins' className='d-flex mt-3'>
                {t('organisations.admins')}
                <span style={{ fontStyle: 'italic' }}>&nbsp;{t('organisations.emailsListInfo')}</span>
              </label>
              <div className='input-group flex-nowrap'>
                <input
                  type='text'
                  className={`form-control my-2`}
                  id='organisationAdmins'
                  placeholder={t('organisations.admins')}
                  value={admins}
                  min={0}
                  onChange={(e) => setAdmins(e.target.value)}
                />
                <OverlayTrigger overlay={<Tooltip id={'seatsTooltip'}>{t('organisations.import-csv')}</Tooltip>}>
                  <span
                    style={{ cursor: 'pointer' }}
                    className='input-group-text input-span-right'
                    id='addon-wrapping'
                    onClick={() => {
                      adminPickerRef.current?.click();
                    }}
                  >
                    <i className='fa-solid fa-file-csv' style={{ fontSize: 25, color: 'black' }} />
                    <input
                      ref={adminPickerRef}
                      type='file'
                      style={{ display: 'none' }}
                      onChange={(e) => handleFileChange(e, csvContent.admins)}
                      accept='.csv'
                    ></input>
                  </span>
                </OverlayTrigger>
              </div>
            </div>
            <div className='form-group'>
              <label htmlFor='organisationAdmins' className='d-flex mt-3'>
                {t('organisations.members')}
                <span style={{ fontStyle: 'italic' }}>&nbsp;{t('organisations.emailsListInfo')}</span>
              </label>
              <div className='input-group flex-nowrap'>
                <input
                  type='text'
                  className={`form-control my-2`}
                  id='organisationAdmins'
                  placeholder={t('organisations.members')}
                  value={members}
                  min={0}
                  onChange={(e) => setMembers(e.target.value)}
                />
                <OverlayTrigger overlay={<Tooltip id={'seatsTooltip'}>{t('organisations.import-csv')}</Tooltip>}>
                  <span
                    style={{ cursor: 'pointer' }}
                    className='input-group-text input-span-right'
                    id='addon-wrapping'
                    onClick={() => {
                      memberPickerRef.current?.click();
                    }}
                  >
                    <i className='fa-solid fa-file-csv' style={{ fontSize: 25, color: 'black' }} />
                    <input
                      ref={memberPickerRef}
                      type='file'
                      style={{ display: 'none' }}
                      onChange={(e) => handleFileChange(e, csvContent.members)}
                      accept='.csv'
                    ></input>
                  </span>
                </OverlayTrigger>
              </div>
            </div>

            <button type='submit' className='btn btn-primary w-100 my-4' disabled={loading}>
              {!loading ? (
                t('organisations.create')
              ) : (
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </div>
              )}
            </button>
            <div className='text-center'>
              <Link to='/orgs'>{t('back')}</Link>
            </div>
          </form>
        </div>
      </div>
    </Container>
  );
};

export default CreateOrganisation;
